/**
 * Email sign-in (magic link + 6-digit code), API-only sign-up and invitations
 * (design 20-identity-design.md §3.4, §4.4; decision §11.3, §11.4).
 *
 * One verified address is one person, and a person belongs to one account (invariant 4
 * in identity.ts). An address is never matched to an account by its domain: a new
 * address signs up a new `email:<ulid>` account, unless an admin of an email account
 * invited it, in which case it joins exactly that account.
 *
 * The link and the code are two ways to prove the same challenge: the link opens the
 * company portal; the code is for the Office add-ins, the Sheets add-on and Teams, which
 * cannot follow a link into their pane (40-client-apps-design.md §2.3).
 */
import type { ClientKind } from './clientApps'
import type { MemberRole, OrgPlatform, Platform } from './identity'
import type { SupportedLocale } from './locale'

/** How long a sign-in link and its code work. */
export const EMAIL_CHALLENGE_TTL_MS = 15 * 60 * 1000

/**
 * Guesses per challenge, counted *before* the guess is checked (the e-signature
 * password rule): a burst of parallel guesses cannot all be evaluated before any
 * counts. Five guesses at a 6-digit code is a 1-in-200,000 chance per challenge.
 */
export const EMAIL_CHALLENGE_MAX_ATTEMPTS = 5

/**
 * Challenges one address may be sent per hour. Bounds the guesses a stranger can buy
 * against one address (five per challenge) and the mail we send to a person who did not
 * ask for it. Past it, /start answers exactly as always and sends nothing.
 */
export const EMAIL_CHALLENGES_PER_ADDRESS_PER_HOUR = 5

/** An invitation is read hours or days later, so it lives far longer than a sign-in link. */
export const EMAIL_INVITATION_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** Invitations an account may send per rolling 24 hours: mail from our domain is our reputation. */
export const EMAIL_INVITATIONS_PER_DAY = 20

/** The longest address RFC 5321 lets through a mail server. */
const MAX_ADDRESS_LENGTH = 254

/**
 * Deliberately plain: one `@`, a dotted domain, no spaces or angle brackets. It does not
 * try to accept every address RFC 5322 allows — the proof is that the mail arrives.
 */
const ADDRESS_RE = /^[^\s@<>()",;:\\[\]]+@[^\s@<>()",;:\\[\]]+\.[^\s@<>()",;:\\[\]]{2,}$/

/**
 * The address as it is stored and compared: trimmed and lowercased. Lowercasing the
 * local part is technically lossy, but no mail provider in use treats case as distinct,
 * and two rows for `Ann@x.com` and `ann@x.com` would be two accounts for one inbox.
 * Null when it is not an address.
 */
export function normalizeEmailAddress(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const address = value.trim().toLowerCase()
  if (address.length === 0 || address.length > MAX_ADDRESS_LENGTH) return null
  return ADDRESS_RE.test(address) ? address : null
}

/** A 6-digit code as typed: digits only, spaces and dashes forgiven (people copy "123 456"). */
export function normalizeEmailCode(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const digits = String(value).replace(/[\s-]/g, '')
  return /^\d{6}$/.test(digits) ? digits : null
}

/** The portal page a sign-in or invitation link opens (hash route: the token never reaches a server log). */
export const EMAIL_AUTH_PORTAL_PATH = '#/auth/email'

export function emailSignInLink(portalOrigin: string, challengeId: string, token: string): string {
  return `${portalOrigin.replace(/\/+$/, '')}/${EMAIL_AUTH_PORTAL_PATH}?c=${encodeURIComponent(challengeId)}&t=${encodeURIComponent(token)}`
}

export function emailInvitationLink(portalOrigin: string, invitationId: string, token: string): string {
  return `${portalOrigin.replace(/\/+$/, '')}/${EMAIL_AUTH_PORTAL_PATH}?i=${encodeURIComponent(invitationId)}&t=${encodeURIComponent(token)}`
}

/**
 * The portal page an invitation into an **organisation** account (Lark, Microsoft, Google)
 * opens. The link alone is not enough there: the person must sign in with the account's
 * platform, as someone of its organisation, and the sign-in carries the invitation
 * (`invitation: { invitationId, token }` in the login body). An email account's invitation
 * keeps emailInvitationLink(), which signs in by itself.
 */
export const INVITATION_PORTAL_PATH = '#/invite'

export function organisationInvitationLink(portalOrigin: string, invitationId: string, token: string): string {
  return `${portalOrigin.replace(/\/+$/, '')}/${INVITATION_PORTAL_PATH}?i=${encodeURIComponent(invitationId)}&t=${encodeURIComponent(token)}`
}

/** The inverse of organisationInvitationLink(), from `location.hash`; null for any other page. */
export function readInvitationLink(hash: string): InvitationProof | null {
  const prefix = `${INVITATION_PORTAL_PATH}?`
  if (!hash.startsWith(prefix)) return null
  const params = new URLSearchParams(hash.slice(prefix.length))
  const token = params.get('t')
  const invitationId = params.get('i')
  if (!token || token.length > 512 || !invitationId || invitationId.length > 128) return null
  return { invitationId, token }
}

/**
 * An invitation as a sign-in carries it: POST /api/auth/login (Lark), /api/auth/microsoft/login
 * and /api/auth/google/login take it as `invitation`. Accepted only when the person signs in
 * as someone of the inviting account's organisation; the address need not match.
 */
export interface InvitationProof {
  invitationId: string
  token: string
}

/** POST /api/auth/invitations/preview — public; the token is the credential. */
export type InvitationPreviewRequest = InvitationProof

/** What the invitation page shows before the person signs in. */
export interface InvitationPreviewResponse {
  accountName: string | null
  /** The platform to sign in with: the account's. */
  platform: Platform
  /** The organisation the person must belong to (null for an email account). */
  organisation: { platform: OrgPlatform; id: string; name: string | null } | null
  invitedEmail: string
  inviterName: string | null
  role: MemberRole
  expiresAt: number
}

/**
 * What an emailed link carries, read back from the portal's address: the inverse of
 * emailSignInLink() / emailInvitationLink(). `hash` is `location.hash`. Null for any
 * other page, or a link missing its token. The answer is exactly the body
 * POST /api/auth/email/verify takes for it.
 */
export type EmailLinkProof = { challengeId: string; token: string } | { invitationId: string; token: string }

export function readEmailAuthLink(hash: string): EmailLinkProof | null {
  const prefix = `${EMAIL_AUTH_PORTAL_PATH}?`
  if (!hash.startsWith(prefix)) return null
  const params = new URLSearchParams(hash.slice(prefix.length))
  const token = params.get('t')
  if (!token || token.length > 512) return null
  const challengeId = params.get('c')
  const invitationId = params.get('i')
  // One or the other: a link naming both was not made by us.
  if (challengeId && !invitationId && challengeId.length <= 128) return { challengeId, token }
  if (invitationId && !challengeId && invitationId.length <= 128) return { invitationId, token }
  return null
}

// =============================================================================
// Error codes (with A's IDENTITY_ERROR_CODES: USER_IN_OTHER_ACCOUNT, MEMBER_REMOVED,
// PLATFORM_MISMATCH, PLATFORM_DISABLED)
// =============================================================================

export const EMAIL_AUTH_ERROR_CODES = {
  /** 400: not an email address. */
  INVALID_EMAIL: 'INVALID_EMAIL',
  /** 403: the Turnstile check failed or was missing. */
  TURNSTILE_FAILED: 'TURNSTILE_FAILED',
  /** 503: the Turnstile check could not be made (not set up here, or Cloudflare unreachable). */
  TURNSTILE_UNAVAILABLE: 'TURNSTILE_UNAVAILABLE',
  /** 429: too many sign-up / sign-in starts from this IP. */
  RATE_LIMITED: 'RATE_LIMITED',
  /** 503: mail cannot be sent from this environment right now (not set up, or the provider refused). */
  EMAIL_UNAVAILABLE: 'EMAIL_UNAVAILABLE',
  /**
   * 503: this environment sent its daily allowance of this kind of mail (sign-in and sign-up
   * codes, or invitations), every address together; it opens again at 00:00 UTC.
   */
  EMAIL_DAILY_LIMIT: 'EMAIL_DAILY_LIMIT',
  /**
   * 400: the challenge is unknown, expired, already used, or out of attempts — one
   * answer for all, so nothing about the challenge is told. The person asks for a new email.
   */
  EMAIL_CHALLENGE_INVALID: 'EMAIL_CHALLENGE_INVALID',
  /** 400: wrong code or link; `attemptsLeft` says how many guesses remain. */
  EMAIL_CODE_INVALID: 'EMAIL_CODE_INVALID',
  /** 400: the invitation is unknown, expired, revoked, already accepted, or its inviter is no longer an admin. */
  INVITATION_INVALID: 'INVITATION_INVALID',
  /** 409: the address is already a member of this account. */
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  /** 429: the account sent EMAIL_INVITATIONS_PER_DAY invitations in the last 24 hours. */
  INVITATION_LIMIT: 'INVITATION_LIMIT',
  /** 404: no pending invitation by that id in this account. */
  INVITATION_NOT_FOUND: 'INVITATION_NOT_FOUND',
  /** 400: seat must be 'starter', 'business' or absent. */
  INVALID_SEAT: 'INVALID_SEAT',
} as const

// =============================================================================
// API shapes
// =============================================================================

/** POST /api/auth/email/start */
export interface EmailStartRequest {
  email: string
  /** The Turnstile widget's token (action `email_start`). */
  turnstileToken: string
  /** Language of the email; the portal's current locale. */
  locale?: SupportedLocale
  /** Used only if this address signs up a new account: the person's name, and the company's. */
  name?: string
  companyName?: string
}

/**
 * Identical whether or not the address has an account, whether or not it was invited,
 * and whether or not this address's hourly allowance of emails was used up — in that
 * last case nothing is sent and the id names no challenge.
 */
export interface EmailStartResponse {
  sent: true
  /** Pass back with the code. Also inside the emailed link. */
  challengeId: string
  expiresAt: number
}

/**
 * POST /api/auth/email/verify — exactly one of:
 * - `{ challengeId, token }` — the emailed link;
 * - `{ challengeId, code }` — the 6-digit code, typed in a client app;
 * - `{ invitationId, token }` — an invitation's link.
 */
export interface EmailVerifyRequest {
  challengeId?: string
  invitationId?: string
  token?: string
  code?: string
  /** The client app proving the code (null/absent: the company portal). */
  client?: ClientKind
}

/**
 * The same shape every client sign-in returns (ClientSessionResponse), plus how the person
 * got in: an existing person signing in, a new account, or joining by invitation.
 */
export interface EmailVerifyResponse {
  token: string
  expiresAt: number
  user: { id: string; name: string; email?: string }
  tenant: { key: string; name: string; platform: 'email'; isNew: boolean }
  isAdmin: boolean
  via: 'signin' | 'signup' | 'invitation'
}

/** One pending invitation (GET /api/tenant/invitations). Shown in People as status Invited. */
export interface EmailInvitationSummary {
  id: string
  email: string
  role: MemberRole
  /** A seat assigned on first sign-in, through the seat rules. */
  seat: 'starter' | 'business' | null
  status: 'invited'
  invitedBy: string
  invitedByName: string | null
  createdAt: number
  expiresAt: number
}

export interface ListInvitationsResponse {
  invitations: EmailInvitationSummary[]
}

/** POST /api/tenant/invitations */
export interface CreateInvitationRequest {
  email: string
  role?: MemberRole
  /** A seat to give them on first sign-in (Starter or Business). */
  seat?: 'starter' | 'business' | null
  /** Language of the invitation email. */
  locale?: SupportedLocale
}

export interface CreateInvitationResponse {
  invitation: EmailInvitationSummary
  /**
   * Set when the seat asked for cannot be given now: every seat of that tier is assigned
   * ('seats_full') or none is in force ('no_seats'). The invitation is sent anyway; the
   * seat is assigned at first sign-in only if one is free then.
   */
  seatWarning?: 'seats_full' | 'no_seats'
}

/** POST /api/tenant/invitations/:id/resend */
export interface ResendInvitationRequest {
  locale?: SupportedLocale
}

// =============================================================================
// Rows (migrations 0062, 0063)
// =============================================================================

/** email_login_challenges — one emailed link + code. Only hashes are stored. */
export interface EmailLoginChallenge {
  id: string
  email: string
  token_hash: string
  /** SHA-256 of `<id>:<code>`: the id salts the 10^6 codes, so one table of hashes fits no other row. */
  code_hash: string
  attempts: number
  /** Used only if the address signs up a new account. */
  signup_name: string | null
  signup_company: string | null
  created_at: number
  expires_at: number
  used_at: number | null
}

/** email_invitations — an admin of an email account inviting an address. */
export interface EmailInvitation {
  id: string
  lark_tenant_key: string
  email: string
  role: MemberRole
  invited_by: string
  token_hash: string
  created_at: number
  expires_at: number
  accepted_at: number | null
  accepted_user_id: string | null
  revoked_at: number | null
  revoked_by: string | null
  /** Migration 0076: a seat to assign on first sign-in, through the seat rules. */
  seat_tier: 'starter' | 'business' | null
  /** Migration 0076: the address the invitee signed in with (an organisation account does not require a match). */
  accepted_email: string | null
}
